import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { FileSearch, Search, Play, ExternalLink, ChevronRight } from "lucide-react";

export default function AdminScans() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [isPaid, setIsPaid] = useState("");
  const [page, setPage] = useState(1);
  const per_page = 30;

  const load = async () => {
    const p = new URLSearchParams({ page: String(page), per_page: String(per_page) });
    if (q) p.set("q", q);
    if (source) p.set("source", source);
    if (isPaid !== "") p.set("is_paid", isPaid);
    try { const { data } = await adminApi.get(`/scans?${p}`); setData(data); }
    catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, source, isPaid]);

  const replay = async (id) => {
    if (!window.confirm("Rejouer ce scan ?")) return;
    try { const { data } = await adminApi.post(`/scans/${id}/replay`);
      toast.success(`Nouveau scan : score ${data.score}/100`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / per_page));

  return (
    <div className="space-y-6" data-testid="admin-scans-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Explorateur</p>
        <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><FileSearch className="w-7 h-7 text-emerald-400" /> Scans</h1>
        <p className="text-sm text-slate-400 mt-1">{data.total.toLocaleString("fr-FR")} scan(s). Filtrables et rejouables.</p>
      </div>

      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-500" />
            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && (setPage(1), load())}
              placeholder="URL / nom fichier…" data-testid="scans-search"
              className="w-full bg-[#0b1220] border border-slate-700 rounded-md pl-8 pr-3 py-2 text-xs" />
          </div>
          <select value={source} onChange={e => { setSource(e.target.value); setPage(1); }} data-testid="scans-source"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="">Toutes sources</option><option value="web">URL/Web</option><option value="image">Image</option><option value="document">Document</option>
          </select>
          <select value={isPaid} onChange={e => { setIsPaid(e.target.value); setPage(1); }} data-testid="scans-paid"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="">Tous</option><option value="true">Payés</option><option value="false">Gratuits</option>
          </select>
          <button onClick={() => (setPage(1), load())} className="px-4 py-2 rounded-md bg-[#1F5A99] text-white text-xs">Filtrer</button>
        </div>

        <div className="overflow-auto max-h-[70vh] border border-slate-800 rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-[#0b1220] sticky top-0">
              <tr className="text-left uppercase tracking-widest text-[10px] text-slate-500">
                <th className="p-3">Date</th><th className="p-3">URL / Fichier</th><th className="p-3">Source</th>
                <th className="p-3">Pages</th><th className="p-3">Score</th><th className="p-3">R/O</th>
                <th className="p-3">Payé</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(s => (
                <tr key={s.id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`scan-row-${s.id.slice(0,8)}`}>
                  <td className="p-3 font-mono text-slate-500">{s.scanned_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="p-3 text-slate-200 max-w-xs truncate">
                    {s.url}
                    {s.playwright_fallback_count > 0 && (
                      <span title={`Playwright fallback utilisé pour ${s.playwright_fallback_count} page(s)`}
                        className="ml-2 px-1.5 py-0.5 text-[9px] rounded bg-blue-500/20 text-blue-300 font-mono uppercase">SPA</span>
                    )}
                  </td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] uppercase">{s.source || "web"}</span></td>
                  <td className="p-3 font-mono">{s.pages_scanned}</td>
                  <td className="p-3 font-mono text-emerald-400">{s.compliance_score || 0}</td>
                  <td className="p-3 font-mono">R{s.red_count} O{s.orange_count}</td>
                  <td className="p-3">{s.is_paid ? <span className="text-emerald-400">✓</span> : <span className="text-slate-500">—</span>}</td>
                  <td className="p-3 flex gap-2">
                    <Link to={`/trust/${s.id}`} target="_blank" className="text-emerald-400 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Trust</Link>
                    {s.is_paid && s.source !== "image" && s.source !== "document" && s.user_id && (
                      <button onClick={() => replay(s.id)} data-testid={`scan-replay-${s.id.slice(0,8)}`}
                        className="text-amber-400 hover:underline inline-flex items-center gap-1"><Play className="w-3 h-3" /> Rejouer</button>
                    )}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">Aucun scan</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4 text-xs">
          <span className="text-slate-500">Page {page}/{totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40">Précédent</button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40">Suivant</button>
          </div>
        </div>
      </div>
    </div>
  );
}
